import { BaseInput, Flex, Popover, RadioButton, RadioGroup, Tag, Text } from "@jsq/ds/core";
import { BackButtonWithinForm } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/OwnersAndControllersCard/CreateOwnersAndControllersFullscreenTakeover/BackButtonWithinForm";
import { ExtendedInfoTooltip } from "~/pages/Compliance/Common/ExtendedInfoTooltip";
import {
  CREATE_NEW_OPTION_ID,
  MIN_SEARCH_LENGTH,
  NONE_OF_THE_ABOVE_OPTION_ID,
  type ProfileExtraOption,
} from "../utils";
import * as styles from "./styles_vanilla.css";
import { findFirstMatch, getListHeader } from "./utils";
import type { ProfileDetail, ProfileListItem } from "./types";

type Props = {
  profiles: ProfileListItem[];
  selectedId: string | null;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSelect: (id: string) => void;
  onBack: () => void;
  hideSearch?: boolean;
  isSearching?: boolean;
  extraOption?: ProfileExtraOption;
};

const HighlightedText = ({ text, query }: { text: string; query: string }) => {
  const textMatch = findFirstMatch(text, query);
  if (!textMatch) return <>{text}</>;

  return (
    <>
      {textMatch.before}
      <Text asSpan variant="strong-md" className={styles.highlight}>
        {textMatch.match}
      </Text>
      {textMatch.after}
    </>
  );
};

const MoreLabel = ({ count }: { count: number }) => (
  <Text asSpan variant="regular-sm" className={styles.detailMore}>
    {` + ${count} more`}
  </Text>
);

const DetailMore = ({ detail }: { detail: ProfileDetail }) => {
  if (!detail.additionalCount) return null;
  if (!detail.tooltip) return <MoreLabel count={detail.additionalCount} />;
  return (
    <ExtendedInfoTooltip message={detail.tooltip} className={styles.preserveLineBreaks}>
      <MoreLabel count={detail.additionalCount} />
    </ExtendedInfoTooltip>
  );
};

const DetailValue = ({ detail }: { detail: ProfileDetail }) => (
  <Text asSpan variant="regular-sm">
    {detail.value}
    <DetailMore detail={detail} />
  </Text>
);

const DetailRow = ({ detail }: { detail: ProfileDetail }) => (
  <Flex className={styles.detailRow}>
    <Text variant="strong-sm" className={styles.detailLabel}>
      {detail.label}
    </Text>
    <DetailValue detail={detail} />
  </Flex>
);

const ProgramTags = ({ programs }: { programs: string[] }) => {
  if (programs.length === 0) return null;
  if (programs.length === 1) return <Tag label={programs[0]} className={styles.tag} />;
  return (
    <Popover>
      <Popover.Trigger>
        <button
          type="button"
          className={styles.programTrigger}
          onClick={(event) => event.stopPropagation()}
        >
          <Tag label={`Multiple programs (${programs.length})`} className={styles.tag} />
        </button>
      </Popover.Trigger>
      <Popover.Content side="right" align="center" sideOffset={4}>
        <span className={styles.popoverArrow} />
        <Flex className={styles.programList}>
          {programs.map((program) => (
            <Tag key={program} variant="neutral" label={program} className={styles.tag} />
          ))}
        </Flex>
      </Popover.Content>
    </Popover>
  );
};

const ProfileOptionContent = ({
  profile,
  searchValue,
}: {
  profile: ProfileListItem;
  searchValue: string;
}) => (
  <Flex className={styles.optionContent}>
    <Flex className={styles.optionInfo}>
      <Text variant="strong-md">
        <HighlightedText text={profile.name} query={searchValue} />
      </Text>
      <Flex className={styles.detailsGrid}>
        {profile.details.map((detail) => (
          <DetailRow key={detail.label} detail={detail} />
        ))}
      </Flex>
      {(profile.lastUsed || profile.accountType) && (
        <Text variant="regular-sm">
          {[profile.lastUsed && `Last used ${profile.lastUsed}`, profile.accountType]
            .filter(Boolean)
            .join("  ·  ")}
        </Text>
      )}
    </Flex>
    {(profile.tags.length > 0 || profile.programs.length > 0) && (
      <Flex className={styles.tagList}>
        {profile.tags.map((tag) => (
          <Tag
            key={tag.label}
            variant={tag.variant ?? "neutral"}
            label={tag.label}
            className={styles.tag}
          />
        ))}
        <ProgramTags programs={profile.programs} />
      </Flex>
    )}
  </Flex>
);

const CreateNewOptionLabel = ({ searchValue }: { searchValue: string }) =>
  searchValue.trim() ? (
    <Text variant="regular-md">
      {"+ Create new profile for “"}
      <Text asSpan variant="strong-md" className={styles.highlight}>
        {searchValue}
      </Text>
      {"”"}
    </Text>
  ) : (
    <Text variant="regular-md">+ Create new profile</Text>
  );

const ExtraOptionRow = ({
  extraOption,
  searchValue,
}: {
  extraOption: ProfileExtraOption;
  searchValue: string;
}) => {
  if (extraOption === "create") {
    return (
      <Flex className={styles.optionRow}>
        <RadioButton value={CREATE_NEW_OPTION_ID}>
          <CreateNewOptionLabel searchValue={searchValue} />
        </RadioButton>
      </Flex>
    );
  }
  if (extraOption === "none-of-the-above") {
    return (
      <Flex className={styles.optionRow}>
        <RadioButton value={NONE_OF_THE_ABOVE_OPTION_ID}>
          <Text variant="regular-md">None of these profiles apply</Text>
        </RadioButton>
      </Flex>
    );
  }
  return null;
};

export const ProfileSelection = ({
  profiles,
  selectedId,
  searchValue,
  onSearchChange,
  onSelect,
  onBack,
  hideSearch = false,
  isSearching = false,
  extraOption = null,
}: Props) => {
  // Gated on !hideSearch so it's correct even if investment-account mode is ever handed an
  // empty profiles list — not just because upstream mode derivation currently prevents that.
  const hasSearchQuery = !hideSearch && searchValue.trim().length >= MIN_SEARCH_LENGTH;
  const hasNoMatches = hasSearchQuery && !isSearching && profiles.length === 0;
  const listHeader = getListHeader(profiles.length > 0, hasNoMatches);

  // "None of the above" always shows in investment-account mode; "Create new" only appears
  // once a real search has run.
  const showExtraOption =
    extraOption === "none-of-the-above" || (extraOption === "create" && hasSearchQuery);

  return (
    <Flex className={styles.container}>
      <Flex className={styles.header}>
        <BackButtonWithinForm onClick={onBack} displayText="Case information" />
        <Flex className={styles.titleBlock}>
          <Text variant="h3" marginBottom="md">
            Compliance profile
          </Text>
          {!hideSearch && (
            <BaseInput
              type="text"
              label="Search existing profile or add new one"
              placeholder="Search by name or identification number (SSN, EIN or ITIN)"
              value={searchValue}
              onSetValue={onSearchChange}
              isLoading={isSearching}
              className={styles.searchInputWrapper}
              isFullWidth
            />
          )}
          {listHeader && <Text variant="secondary-md">{listHeader}</Text>}
        </Flex>
      </Flex>

      <RadioGroup
        name="profile-selection"
        value={selectedId ?? ""}
        onChange={(event) => onSelect(event.target.value)}
        className={styles.optionList}
      >
        {profiles.map((profile) => (
          <Flex key={profile.id} className={styles.optionRow}>
            <RadioButton value={profile.id}>
              <ProfileOptionContent profile={profile} searchValue={searchValue} />
            </RadioButton>
          </Flex>
        ))}
        {showExtraOption && (
          <ExtraOptionRow extraOption={extraOption} searchValue={searchValue} />
        )}
      </RadioGroup>
    </Flex>
  );
};
